import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, Box } from '@frontierag/frontier-ui-library';
import { ActionRequiredModal, useWizardData } from 'components';
import DataAccordionColumns from 'components/DataAccordionColumns';
import DataAccordionTitle from 'components/DataAccordionTitle';
import DataColumns from 'components/DataColumns';
import { useAppSettings } from 'contexts/App/useAppSettings';
import useOrderDetails from 'hooks/useOrderDetails';
import useOrderDetailsFields from 'hooks/useOrderDetailsFields';

export default function OrderSummary() {
  const { t } = useTranslation('CreateOrder');
  const { values } = useWizardData();
  const { FERTILISER: { liquidNotificationDateRange } = {} } = useAppSettings();

  const { subGroups = [] } = liquidNotificationDateRange || {};

  const isLiquidNProduct = useCallback(
    (subGroupDescription: string) =>
      subGroupDescription && Array.isArray(subGroups) && subGroups.includes(subGroupDescription),
    [subGroups],
  );
  const photoIdRequiredProductNames = useMemo(
    () =>
      values.products.reduce(
        (acc: string[], curr) =>
          curr.productData.requiredPhotoId
            ? [...acc, curr.productData.packagedItemDescription]
            : acc,
        [],
      ),
    [values.products],
  );

  const limusPerformRequiredProductNames: string[] = useMemo(() => {
    if (!values || !values.products || !liquidNotificationDateRange) return [];
    return values.products.reduce(
      (acc: string[], curr) =>
        isLiquidNProduct(curr.productData.subGroupDescription)
          ? [...acc, curr.productData.packagedItemDescription]
          : acc,
      [],
    );
  }, [isLiquidNProduct, liquidNotificationDateRange, values]);

  const showModalForPhotoId = useMemo(() => {
    if (!values) return false;
    return (
      values.customer.hasSubmittedPhotoId === false &&
      values.products.some((product) => product.productData.requiredPhotoId === true)
    );
  }, [values]);

  const showModalForLimusPerform = useMemo(
    () => limusPerformRequiredProductNames.length > 0,
    [limusPerformRequiredProductNames.length],
  );

  const [isModalOpen, setModalOpen] = useState(showModalForPhotoId || showModalForLimusPerform);

  const handlePhotoIdClose = useCallback(() => {
    setModalOpen(false);
  }, [setModalOpen]);

  const orderDetailsFields = useOrderDetailsFields('CreateOrder', true);

  const schemaExternalDeps = useMemo(
    () => ({
      isDebtOwner: values.debtOwner === 'true',
    }),
    [values],
  );

  const fieldsSchema = useOrderDetails({}, schemaExternalDeps, true);

  const accordionSubHeader = useMemo(
    () => (
      <Box pb={2}>
        <Alert severity="warning">{t('OrderSummary.notifications.orderNotConfirmed')}</Alert>
      </Box>
    ),
    [t],
  );

  return (
    <Box data-testid="order-summary-page">
      {values.products.map((product) => (
        <Box pb={{ xs: 2, sm: 3 }} key={`accordion-${product.internalOrderId}`}>
          <DataAccordionColumns
            fieldsSchema={fieldsSchema}
            data={product}
            title={
              <DataAccordionTitle text={product?.productData?.packagedItemDescription} id="" />
            }
            subHeader={accordionSubHeader}
            key={product.internalOrderId}
            name={`product-${product.internalOrderId}`}
          />
        </Box>
      ))}
      <Box>
        <DataColumns data={values} columnsSchema={orderDetailsFields} />
      </Box>
      {isModalOpen && (
        <ActionRequiredModal
          open
          onClose={handlePhotoIdClose}
          products={photoIdRequiredProductNames}
          limusPerformProducts={limusPerformRequiredProductNames}
        />
      )}
    </Box>
  );
}
